"use client";

import { useMemo, useState } from "react";
import { ChevronDown, SearchIcon } from "@/components/icons/icons";
import type { BlogPost } from "@/lib/blog-types";
import { BlogListCard } from "@/components/sections/blog-card";
import type { Dictionary } from "@/i18n/dictionaries";
import type { Locale } from "@/i18n/config";

type BlogExplorerProps = {
  posts: BlogPost[];
  dict: Dictionary;
  locale: Locale;
};

export function BlogExplorer({ posts, dict, locale }: BlogExplorerProps) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>(dict.blog.allCategories);

  const categoryOptions = useMemo(() => {
    const allFromPosts = new Set(posts.map((p) => p.category));
    return [
      dict.blog.allCategories,
      ...Array.from(allFromPosts).filter(Boolean),
    ];
  }, [posts, dict.blog.allCategories]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      if (category !== dict.blog.allCategories && p.category !== category)
        return false;
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q)
      );
    });
  }, [posts, query, category, dict.blog.allCategories]);

  return (
    <>
      <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
        <label className="relative flex flex-1 items-center">
          <span className="sr-only">{dict.blog.searchPlaceholder}</span>
          <SearchIcon className="pointer-events-none absolute left-4 size-5 text-neutral-400" />
          <input
            type="search"
            placeholder={dict.blog.searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-11 w-full rounded-full border border-neutral-200 bg-white pl-11 pr-4 text-sm tracking-tight text-neutral-950 shadow-[var(--shadow-xs)] placeholder:text-neutral-400 focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand-ring"
          />
        </label>
        <div className="relative w-full sm:w-60">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label="Filter by category"
            className="h-11 w-full appearance-none rounded-full border border-neutral-200 bg-white pl-4 pr-10 text-sm tracking-tight text-neutral-950 shadow-[var(--shadow-xs)] hover:border-neutral-300 focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand-ring"
          >
            {categoryOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-4 top-1/2 size-5 -translate-y-1/2 text-neutral-500"
            aria-hidden
          />
        </div>
      </div>

      <div className="mt-12 sm:mt-16">
        {filtered.length === 0 ? (
          <p className="rounded-xl border border-dashed border-neutral-200 bg-white p-8 text-center text-base text-neutral-500">
            No posts match your filters.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-x-12 gap-y-12 md:grid-cols-2">
            {filtered.map((post) => (
              <li key={post.slug}>
                <BlogListCard post={post} locale={locale} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
